/**
 * PlannerPreviewDashboard – Read-only preview of planner's estate data.
 * Figma Frame 378 (node 1018:15426): Activity view showing category cards
 * and general updates feed.
 */

import { useState, useEffect, useCallback } from 'react';
import PlannerDataBadge from './PlannerDataBadge';
import { apiService } from '../../utils/api';

interface PlannerSection {
  id: string;
  label: string;
  frameId: string;
}

const PLANNER_SECTIONS: PlannerSection[] = [
  { id: 'funeral', label: 'Plan for Funeral & Memorial', frameId: 'frame-219' },
  { id: 'debts', label: 'Track Debts & Financial Expenses', frameId: 'frame-143' },
  { id: 'legal', label: 'Legal Essentials', frameId: 'frame-214' },
];

interface PlannerPreviewDashboardProps {
  estateId: string;
  estateOwnerName: string;
  isPostDeath?: boolean;
}

export default function PlannerPreviewDashboard({
  estateId,
  estateOwnerName,
  isPostDeath = false,
}: PlannerPreviewDashboardProps) {
  const [sectionData, setSectionData] = useState<Record<string, { hasData: boolean; updates?: string[] }>>({});
  const [loading, setLoading] = useState(true);

  const loadSections = useCallback(async () => {
    setLoading(true);
    const results: Record<string, { hasData: boolean; updates?: string[] }> = {};

    for (const section of PLANNER_SECTIONS) {
      try {
        const response = await apiService.request(
          `/api/settlement/${estateId}/planner-data/${section.frameId}`,
          { method: 'GET' }
        );
        if (response.success && response.data) {
          results[section.id] = { hasData: true, updates: response.updates || [] };
        } else {
          results[section.id] = { hasData: false };
        }
      } catch {
        results[section.id] = { hasData: false };
      }
    }

    setSectionData(results);
    setLoading(false);
  }, [estateId]);

  useEffect(() => {
    loadSections();
  }, [loadSections]);

  return (
    <div className="max-w-4xl mx-auto">
      {/* Viewing banner — Figma: amber/yellow pill */}
      <div className="bg-[#FFF3CD] rounded-full px-5 py-2 mb-6 text-center max-w-lg mx-auto">
        <p className="text-sm text-[#856404]">
          <span className="mr-1">ⓘ</span> Viewing as Executor for <strong>{estateOwnerName}'s</strong> Estate
        </p>
      </div>

      {/* Title — Figma: serif, centered */}
      <h2 className="font-display text-[28px] leading-tight text-blue-900 mb-2 text-center">
        {estateOwnerName}'s Activity
      </h2>
      <p className="text-sm text-grey-600 mb-6 text-center">
        See what the planner has done since your last login.
      </p>

      {loading ? (
        <div className="text-center py-12 text-grey-600">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3" />
          Loading estate plan data...
        </div>
      ) : (
        <>
          {/* Category cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {PLANNER_SECTIONS.map((section) => {
              const data = sectionData[section.id];
              return (
                <div
                  key={section.id}
                  className="rounded-xl border border-grey-300 bg-white-alt p-5"
                >
                  <h3 className="font-semibold text-sm text-black mb-3">{section.label}</h3>
                  {data?.hasData && data.updates && data.updates.length > 0 ? (
                    <ul className="space-y-2">
                      {data.updates.map((update, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="w-2 h-2 rounded-full bg-blue-600 mt-1.5 shrink-0" />
                          <span className="text-xs text-grey-600">{update}</span>
                        </li>
                      ))}
                    </ul>
                  ) : data?.hasData ? (
                    <div className="mt-1">
                      <PlannerDataBadge variant="from-planning" className="text-[10px]" />
                    </div>
                  ) : (
                    <p className="text-xs text-grey-600">No updates</p>
                  )}
                </div>
              );
            })}
          </div>

          {/* General Updates feed */}
          {!isPostDeath && (
            <div className="rounded-xl border border-orange-300 bg-orange-100/30 p-5">
              <h3 className="font-semibold text-base text-black text-center mb-4">General Updates</h3>
              <p className="text-sm text-grey-600 text-center">
                Activity updates will appear here as the planner makes changes.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
